import http from "@/services/http";
import { featureFlags } from "@/config/env";
import { mockManager } from "@/services/mock";
import { getSourceId } from "@/utils/constants";

/**
 * 品牌数据指标比率接口
 */
export interface BrandMetricsRates {
  mentionRate: number; // 提及率（保留2位小数）
  firstMentionRate: number; // 首位提及率（保留2位小数）
  firstRecommendationRate: number; // 首位推荐率（保留2位小数）
  positiveMentionRate: number; // 正面提及率（保留2位小数）
  negativeMentionRate: number; // 负面提及率（保留2位小数）
  statisticsPeriodStart: string; // 统计开始时间（YYYY-MM-DD）
  statisticsPeriodEnd: string; // 统计结束时间（YYYY-MM-DD）
}

/**
 * 品牌数据指标比率API响应接口
 */
export interface BrandMetricsRatesResponse {
  code: number;
  message: string;
  data: BrandMetricsRates;
}

/**
 * 请求参数接口
 */
export interface BrandMetricsRatesParams {
  brandId: string; // 品牌ID（必填）
  categoryId: string; // 分类ID（必填）
  days?: number; // 日期范围：1=昨天，7=近7天，30=近30天，默认1
  startDay?: string; // 自定义开始日期，格式：2025-12-30
  endDay?: string; // 自定义结束日期，格式：2025-12-30
  modelId?: string; // 模型ID，全部时传"all"，默认"all"
  qId?: string; // 问题ID
  sourceId?: number; // 数据源ID：2=国内，3=国外
}

/**
 * 品牌数据指标比率API
 */
export const brandMetricsRatesApi = {
  /**
   * 获取品牌数据指标比率
   * @param params 请求参数
   * @returns Promise<BrandMetricsRates | null> 品牌数据指标比率
   */
  async getBrandMetricsRates(
    params: BrandMetricsRatesParams
  ): Promise<BrandMetricsRates | null> {
    try {
      // 使用统一的 Mock 数据管理器
      console.log("BrandMetricsRates API request params:", {
        brandId: params.brandId,
        categoryId: params.categoryId,
        days: params.days || 1,
        modelId: params.modelId || "all",
      });

      if (featureFlags.enableMock) {
        return await mockManager.getBrandMetrics(
          params.brandId,
          params.categoryId,
          params.days || 7,
          params.modelId || "all"
        );
      }

      // 自动添加sourceId，如果没有指定则使用默认值
      const sourceId = params.sourceId ?? getSourceId();

      // 构建查询参数
      const queryParams: Record<string, string> = {
        brandId: params.brandId,
        categoryId: params.categoryId,
        days: (params.days || 1).toString(),
        modelId: params.modelId || "all",
        sourceId: sourceId.toString(),
      };

      // 添加可选参数
      if (params.startDay) {
        queryParams.startDay = params.startDay;
      }
      if (params.endDay) {
        queryParams.endDay = params.endDay;
      }
      if (params.qId) {
        queryParams.qId = params.qId;
      }

      const urlParams = new URLSearchParams(queryParams);

      const response = await http.get<BrandMetricsRatesResponse>(
        `data/brand/metrics/rates?${urlParams.toString()}`
      );

      console.log("BrandMetricsRates API response:", response);

      // 检查响应结构
      if (!response || !response.data) {
        console.error("No response data:", response);
        return null;
      }

      const payload = response.data as any;
      console.log("BrandMetricsRates response data:", payload);

      // 兼容两种结构：
      // 1) { code, message, data: { mentionRate, ... } }
      // 2) { mentionRate, ... }
      if (payload && typeof payload === "object") {
        if (
          "data" in payload &&
          payload.data &&
          typeof payload.data === "object"
        ) {
          return payload.data as BrandMetricsRates;
        }
        if ("mentionRate" in payload) {
          return payload as BrandMetricsRates;
        }
      }
      console.error(
        "Invalid BrandMetricsRates API response structure:",
        payload
      );
      return null;
    } catch (error) {
      // 错误处理
      console.error("BrandMetricsRates API error:", error);

      // 如果是API业务错误，错误信息已经在HTTP拦截器中显示
      // 这里返回null，避免重复显示错误信息
      return null;
    }
  },
};

export default brandMetricsRatesApi;
